import { isApiError } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';

/**
 * The words for a refused start. A 402 carries the quote, the balance and
 * the shortfall, and the person needs those numbers more than the code.
 */
export function startErrorMessage(t: Translate, err: unknown): string {
  if (isApiError(err) && err.status === 402) {
    const body = err.body as { error?: { estimated_minor?: number; available_minor?: number; shortfall_minor?: number; currency?: string; recipients?: number } } | null;
    const e = body?.error;
    if (e && typeof e.shortfall_minor === 'number') {
      return t('broadcasts.start.insufficient', {
        estimate: formatCredits(e.estimated_minor ?? 0, t),
        balance: formatCredits(e.available_minor ?? 0, t),
        shortfall: formatCredits(e.shortfall_minor, t),
        count: e.recipients ?? 0
      });
    }
  }
  // WhatsApp's daily limit on new people for the number (2 October 2026).
  if (isDailyLimit(err)) {
    const e = (err.body as { error?: { recipients?: number; remaining?: number; limit?: number } } | null)?.error;
    return t('broadcasts.start.dailyLimit', { count: e?.recipients ?? 0, remaining: e?.remaining ?? 0, limit: e?.limit ?? 0 });
  }
  // SMS (4 October 2026): the two refusals an SMS broadcast can meet.
  if (isApiError(err) && err.code === 'SMS_BODY_REQUIRED') return t('broadcasts.sms.error.bodyRequired');
  if (isApiError(err) && err.code === 'SMS_UNAVAILABLE') {
    return err.status === 503 ? t('broadcasts.sms.error.tryAgain') : t('broadcasts.sms.error.unavailable');
  }
  // Email (4 October 2026): the three refusals an email broadcast can meet.
  if (isApiError(err) && err.code === 'EMAIL_CONTENT_REQUIRED') return t('broadcasts.email.error.contentRequired');
  if (isApiError(err) && err.code === 'EMAIL_BUTTON_LINK') return t('broadcasts.email.error.buttonLink');
  if (isApiError(err) && err.code === 'EMAIL_UNAVAILABLE') {
    return err.status === 503 ? t('broadcasts.email.error.tryAgain') : t('broadcasts.email.error.unavailable');
  }
  // Finished (4 October 2026): a broadcast runs once. Resend to failed makes a new one for the people it missed.
  if (isApiError(err) && err.code === 'BROADCAST_NOT_STARTABLE') return t('broadcasts.start.notStartable');
  if (isApiError(err) && err.code === 'BROADCAST_FINISHED') return t('broadcasts.cancel.finished');
  return err instanceof Error ? err.message : t('broadcasts.view.lifecycle.toast.startFailed');
}

/** The words for a refused cancel: once it has finished there is nothing left to stop (4 October 2026). */
export function cancelErrorMessage(t: Translate, err: unknown): string {
  if (isApiError(err) && err.code === 'BROADCAST_FINISHED') return t('broadcasts.cancel.finished');
  if (isApiError(err) && err.code === 'BROADCAST_NOT_STARTABLE') return t('broadcasts.start.notStartable');
  return err instanceof Error ? err.message : t('broadcasts.view.lifecycle.toast.cancelFailed');
}

/** A start refused because the audience is bigger than the number may reach today. */
export function isDailyLimit(err: unknown): err is { status: number; body: unknown; code: string } {
  return isApiError(err) && err.code === 'NUMBER_DAILY_LIMIT';
}
